import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Pen, Upload, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth is via httpOnly cookie (withCredentials)
const getAxiosConfig = () => ({ withCredentials: true });

const SignatureUpload = ({ isOpen, onClose, onSignatureUpdated, targetUser = null }) => {
  // targetUser = { id, full_name, role } - if provided, admin is uploading for this user
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const isAdminMode = targetUser !== null;

  const fetchCurrentSignature = useCallback(async () => {
    try {
      const url = isAdminMode 
        ? `${API}/admin/users/${targetUser.id}/signature`
        : `${API}/auth/signature`;
      const response = await axios.get(url, getAxiosConfig());
      if (response.data.signature) {
        setPreviewUrl(response.data.signature);
      } else {
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch signature');
      setPreviewUrl(null);
    }
  }, [isAdminMode, targetUser]);

  useEffect(() => {
    if (isOpen) {
      setSignature(null);
      fetchCurrentSignature();
    }
  }, [isOpen, targetUser, fetchCurrentSignature]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG/PNG)');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500000) {
      toast.error('Image must be less than 500KB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setSignature(event.target.result);
      setPreviewUrl(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!signature) {
      toast.error('Please select a signature image first');
      return;
    }

    setLoading(true);
    try {
      const url = isAdminMode 
        ? `${API}/admin/users/${targetUser.id}/signature`
        : `${API}/auth/signature`;
      await axios.put(url, { signature }, getAxiosConfig());
      toast.success(isAdminMode 
        ? `Signature uploaded for ${targetUser.full_name}!`
        : 'Signature uploaded successfully!');
      onSignatureUpdated?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload signature');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmMsg = isAdminMode 
      ? `Are you sure you want to delete ${targetUser.full_name}'s signature?`
      : 'Are you sure you want to delete your signature?';
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      const url = isAdminMode 
        ? `${API}/admin/users/${targetUser.id}/signature`
        : `${API}/auth/signature`;
      await axios.delete(url, getAxiosConfig());
      setSignature(null);
      setPreviewUrl(null);
      toast.success(isAdminMode ? `Signature deleted for ${targetUser.full_name}` : 'Signature deleted');
      onSignatureUpdated?.();
    } catch (error) {
      toast.error('Failed to delete signature');
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle = isAdminMode 
    ? `Upload Signature for ${targetUser.full_name}`
    : 'Upload Your Signature';

  const description = isAdminMode
    ? `Upload a JPEG/PNG signature for ${targetUser.full_name} (${targetUser.role}). This will be used when they verify or approve vouchers.`
    : 'Upload a JPEG/PNG image of your signature. This will be used when you verify or approve vouchers.';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pen className="w-5 h-5 text-green-600" />
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">{description}</p>

          {/* Preview Area */}
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center bg-slate-50 min-h-[120px] flex items-center justify-center">
            {previewUrl ? (
              <div className="relative">
                <img 
                  src={previewUrl} 
                  alt="Signature Preview" 
                  className="max-h-24 max-w-full object-contain"
                />
                <button
                  onClick={() => { setSignature(null); setPreviewUrl(null); }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="text-slate-400">
                <Pen className="w-12 h-12 mx-auto mb-2" />
                <p>No signature uploaded</p>
              </div>
            )}
          </div>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              <Upload className="w-4 h-4 mr-2" />
              Select Image
            </Button>

            {previewUrl && (
              <Button
                onClick={handleDelete}
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Upload Button */}
          {signature && (
            <Button
              onClick={handleUpload}
              className="w-full bg-green-500 hover:bg-green-600"
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Save Signature'}
            </Button>
          )}

          <p className="text-xs text-slate-400 text-center">
            Supported formats: JPEG, PNG (Max 500KB)
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureUpload;
